/**
 * Scrub PII/volatile data from diagnostic messages before they leave the device.
 */

const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const TOKEN_RE = /\b(?:bearer\s+)?[a-zA-Z0-9_-]{24,}\b/gi;
const MAC_HOME_RE = /\/Users\/[^/\s]+/g;
const LINUX_HOME_RE = /\/home\/[^/\s]+/g;
const WIN_HOME_RE = /[A-Za-z]:\\Users\\[^\\]+/g;
const URL_RE = /https?:\/\/[^\s)]+/gi;
const JWT_RE = /eyJ[A-Za-z0-9_-]+\.eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;
const AUTH_JSON_FIELD_RE =
  /"(?:refresh_token|access_token|password|api_key|client_secret|token)"\s*:\s*"[^"]*"/gi;
const AUTH_JSON_PATH_RE = /auth\.json[^\n]*/gi;
const MAESTRO_AUTH_LS_RE = /maestro_auth[^\n]*/gi;

export function sanitizeDiagnosticText(text: string): string {
  if (!text) return '';
  let s = String(text);
  s = s.replace(MAC_HOME_RE, '~');
  s = s.replace(LINUX_HOME_RE, '~');
  s = s.replace(WIN_HOME_RE, '~');
  s = s.replace(URL_RE, (url) => url.split('?')[0]);
  s = s.replace(EMAIL_RE, '[redacted]');
  s = s.replace(TOKEN_RE, '[redacted]');
  return s.slice(0, 4000);
}

const SUPPORT_LOG_MAX_CHARS = 500_000;
const CONNECTOR_TAIL_MARKER = '=== Connector log tail ===';

function scrubSupportLogLine(line: string): string {
  let s = line;
  s = s.replace(MAC_HOME_RE, '~');
  s = s.replace(LINUX_HOME_RE, '~');
  s = s.replace(WIN_HOME_RE, '~');
  s = s.replace(URL_RE, (url) => url.split('?')[0]);
  s = s.replace(EMAIL_RE, '[redacted]');
  s = s.replace(TOKEN_RE, '[redacted]');
  return s;
}

/** Full support-log scrub — applied to entire .txt before upload on every ticket. */
export function sanitizeSupportLogText(text: string): string {
  if (!text) return '';
  // Never run sanitizeDiagnosticText() on the whole bundle — its 4k cap was
  // truncating everything after the console buffer, dropping the Connector tail.
  let s = text
    .split('\n')
    .map(scrubSupportLogLine)
    .join('\n');
  s = s.replace(JWT_RE, '[redacted-jwt]');
  s = s.replace(AUTH_JSON_FIELD_RE, (match) => {
    const key = match.split(':')[0];
    return `${key}: "[redacted]"`;
  });
  s = s.replace(AUTH_JSON_PATH_RE, 'auth.json [redacted]');
  s = s.replace(MAESTRO_AUTH_LS_RE, 'maestro_auth [redacted]');

  if (s.length <= SUPPORT_LOG_MAX_CHARS) return s;

  const tailIdx = s.indexOf(CONNECTOR_TAIL_MARKER);
  if (tailIdx >= 0) {
    const preservedTail = s.slice(tailIdx);
    const headBudget = SUPPORT_LOG_MAX_CHARS - preservedTail.length - 80;
    if (headBudget > 0) {
      return `${s.slice(0, headBudget)}\n… [earlier log truncated — connector tail preserved]\n\n${preservedTail}`;
    }
    return preservedTail.slice(0, SUPPORT_LOG_MAX_CHARS);
  }

  return `${s.slice(0, SUPPORT_LOG_MAX_CHARS)}\n… [log truncated]\n`;
}

export function sanitizeStack(stack: string | undefined, maxFrames = 8): string | undefined {
  if (!stack) return undefined;
  const frames = stack.split('\n').slice(0, maxFrames);
  return sanitizeDiagnosticText(frames.join('\n'));
}

export function sanitizePayload(payload: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (value == null) continue;
    if (typeof value === 'string') {
      out[key] = key === 'stack' || key === 'component_stack'
        ? sanitizeStack(value)
        : sanitizeDiagnosticText(value);
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      out[key] = value;
    }
  }
  return out;
}
